import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { MagnifyingGlass, Sparkle, Plus, Books, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

function ScoreBar({ value, max = 5, color = "var(--sage)" }) {
    const v = value || 0;
    return (
        <div className="inline-flex items-center gap-0.5" title={`${v}/${max}`}>
            {Array.from({ length: max }).map((_, i) => (
                <span key={i} className="inline-block w-1.5 h-3" style={{ background: i < v ? color : "var(--line)" }}/>
            ))}
        </div>
    );
}

export default function Compounds() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [category, setCategory] = useState("");
    const [categories, setCategories] = useState([]);
    const [refreshing, setRefreshing] = useState(false);
    const [ingesting, setIngesting] = useState({});

    const load = async () => {
        const [c, cats] = await Promise.all([
            api.get("/compounds", { params: { q: q || undefined, category: category || undefined } }),
            api.get("/compounds/categories"),
        ]);
        setItems(c.data);
        setCategories(cats.data);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, category]);

    const refreshScores = async () => {
        if (!window.confirm("Recalcular evidencia y popularidad para todos los compuestos? Puede tardar 1-3 minutos.")) return;
        setRefreshing(true);
        try {
            const { data } = await api.post("/compounds/refresh-scores/bulk");
            toast.success(`Scores actualizados — ${data.processed} compuestos`);
            await load();
        } catch (e) {
            toast.error("Error recalculando scores");
        } finally { setRefreshing(false); }
    };

    const ingestEvidence = async (slug, name) => {
        setIngesting((s) => ({ ...s, [slug]: true }));
        try {
            const { data } = await api.post(`/compounds/${slug}/ingest-evidence`);
            toast.success(`${name}: ${data.new_count} referencias nuevas (${data.total_evidence} en total)`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || `Error buscando evidencia para ${name}`);
        } finally {
            setIngesting((s) => ({ ...s, [slug]: false }));
        }
    };

    return (
        <div className="fade-up">
            <PageHeader
                eyebrow={`Catálogo · ${items.length} principios activos`}
                title="Principios activos"
                lead="Cada compuesto recoge indicaciones, dosis por indicación, contraindicaciones, límites toxicológicos y evidencia con enlaces a la fuente real."
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={refreshScores} disabled={refreshing} className="px-4 py-2.5 border hairline bg-white text-sm hover:bg-[var(--cream)] inline-flex items-center gap-2 disabled:opacity-50" data-testid="compounds-refresh-scores-button">
                            <Sparkle size={14} weight="bold"/> {refreshing ? "Recalculando…" : "Recalcular scores"}
                        </button>
                        <Link to="/compuestos/nuevo" className="btn-primary inline-flex items-center gap-2" data-testid="compounds-new-button">
                            <Plus size={14} weight="bold"/> Nuevo principio
                        </Link>
                    </div>
                }
            />

            <div className="px-8 md:px-12 py-8 max-w-6xl">
                <div className="flex flex-wrap gap-3 mb-6">
                    <div className="relative flex-1 min-w-[220px]">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
                        <input
                            className="w-full pl-9 pr-4 py-2.5 border hairline bg-white text-sm focus:outline-none focus:border-[var(--sage)]"
                            placeholder="Buscar por nombre…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="compounds-search-input"
                        />
                    </div>
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="px-3 py-2.5 border hairline bg-white text-sm focus:outline-none focus:border-[var(--sage)]"
                        data-testid="compounds-category-filter"
                    >
                        <option value="">Todas las categorías</option>
                        {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>

                <div className="surface overflow-x-auto" data-testid="compounds-table-wrap">
                    <table className="data-table w-full">
                        <thead>
                            <tr>
                                <th>Compuesto</th>
                                <th>Categoría</th>
                                <th>Indicaciones</th>
                                <th>UL (mg)</th>
                                <th>Evidencia</th>
                                <th>Popularidad</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.slug} data-testid={`compound-row-${c.slug}`}>
                                    <td>
                                        <Link to={`/compuestos/${c.slug}`} className="font-medium hover:text-[var(--sage)]">
                                            {c.name}
                                        </Link>
                                        {c.aka?.length > 0 && (
                                            <div className="text-xs text-[var(--ink-3)] mt-0.5">{c.aka.join(" · ")}</div>
                                        )}
                                    </td>
                                    <td className="text-[var(--ink-2)]">{c.category}</td>
                                    <td className="text-[var(--ink-2)] max-w-[280px]">
                                        <div className="truncate">{(c.indications || []).slice(0, 3).join(" · ")}</div>
                                    </td>
                                    <td className="mono text-xs">{c.upper_limit_mg ?? "—"}</td>
                                    <td><div className="flex items-center gap-2"><ScoreBar value={c.evidence_score} color="var(--sage)"/><span className="text-xs mono text-[var(--ink-3)]">{(c.evidence || []).length}</span></div></td>
                                    <td>{c.popularity_score ? <ScoreBar value={c.popularity_score} color="var(--terra)"/> : <span className="text-xs text-[var(--ink-3)] mono">—</span>}</td>
                                    <td>
                                        <button
                                            onClick={() => ingestEvidence(c.slug, c.name)}
                                            disabled={!!ingesting[c.slug]}
                                            title="Buscar evidencia científica (PubMed, Europe PMC, ClinicalTrials)"
                                            className="px-2.5 py-1.5 border hairline bg-white text-xs hover:bg-[var(--cream)] inline-flex items-center gap-1.5 disabled:opacity-50 whitespace-nowrap"
                                            data-testid={`compound-ingest-evidence-${c.slug}`}
                                        >
                                            {ingesting[c.slug]
                                                ? <><CircleNotch size={13} className="animate-spin"/> Buscando…</>
                                                : <><Books size={13} weight="bold"/> Evidencia</>}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr><td colSpan={7} className="text-center text-[var(--ink-3)] py-10">Sin resultados</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
